import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import { useEarningsInsightsByEarningsId } from '@terminal/react-query-hooks/insights/useEarningsInsightsByEarningsId';
import { getFiscalDateInfo } from '@terminal/views/theme-overview/tab-content/earnings/utils/utils';
import { useMemo } from 'react';

/**
 * Fetches and returns the most relevant earnings insight for a given entity.
 *
 * Loads earnings for the entity first (current fiscal quarter), then insights
 * scoped to those earnings_ids. Pairs the first earnings row with its insight.
 */
export const useEarningsInsightsByEntity = (entityTag: string) => {
  const { startDate, endDate } = getFiscalDateInfo();

  const { data: earningsQueryResult, isFetching: isEarningsFetching } =
    useEarningsFilter({
      body: {
        entities: [entityTag],
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      },
      shouldFetchEntities: false,
    });

  const earningsList = useMemo(
    () => earningsQueryResult?.pages.flatMap(page => page) ?? [],
    [earningsQueryResult?.pages]
  );

  const earningsIds = useMemo(
    () => earningsList.map(earning => earning.id),
    [earningsList]
  );

  const { earningsInsightByEarningsId, isEarningsInsightsFetching } =
    useEarningsInsightsByEarningsId({ earningsIds });

  const latestEarningsData = earningsList[0];
  const earningsInsight =
    latestEarningsData &&
    earningsInsightByEarningsId.get(latestEarningsData.id);

  const isPreview =
    earningsInsight?.type === 'preview' ||
    (latestEarningsData && !latestEarningsData.reported);

  const isLoading = isEarningsFetching || isEarningsInsightsFetching;

  return {
    isLoading,
    latestEarningsData,
    earningsInsight,
    isPreview,
  };
};
